import {
    Component,
    Input,
    OnInit,
    ChangeDetectorRef,
    OnDestroy,
    forwardRef,
    Output,
    EventEmitter,
    SimpleChanges,
    OnChanges,
    ChangeDetectionStrategy,
    NgZone,
} from '@angular/core';
import {
    FormControl,
    ReactiveFormsModule,
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { debounceTime } from 'rxjs/operators';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
@Component({
    selector: 'mat-select-search',
    templateUrl: './mat-select-search.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatSelectModule,
        NgxMatSelectSearchModule,
        MatTooltipModule,
        MatIconModule,
        MatProgressSpinnerModule,
        TranslocoModule,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MatSelectSearchComponent),
            multi: true,
        },
    ],
})
export class MatSelectSearchComponent
    implements OnInit, OnDestroy, ControlValueAccessor, OnChanges
{
    @Input() options: any[] = [];
    @Input() placeholder: string = 'Select';
    @Input() searchPlaceholder: string = '';
    @Input() width: string = '';
    @Input() label: string = '';
    @Input() multiple: boolean = false;
    @Input() displayField: string = 'name';
    /** Optional second line shown below displayField in each option. */
    @Input() subtitleField: string = '';
    /** Field shown in the closed select trigger; defaults to displayField. */
    @Input() triggerDisplayField: string = '';
    @Input() valueField: string = 'id';
    @Input() selectAll: boolean = false;
    @Input() tooltipInfo: string = '';
    @Input() from: string = '';
    /** When true, skip client-side filtering and emit `searchChange` for server-backed option lists. */
    @Input() serverSide: boolean = false;
    /** When true with `serverSide`, emit `loadMore` when the panel is scrolled near the bottom. */
    @Input() hasMore: boolean = false;
    @Input() loadingMore: boolean = false;
    @Input() formControl: FormControl<any> = new FormControl<any>(null);
    @Output() selectionChange = new EventEmitter<any>();
    @Output() selectionObjectsChange = new EventEmitter<any[]>();
    @Output() searchChange = new EventEmitter<string>();
    @Output() loadMore = new EventEmitter<void>();
    lang: string = 'en';

    /** Bound directly in the template (avoids async-pipe re-render scroll jumps). */
    displayOptions: any[] = [];
    public filterCtrl: FormControl<string> = new FormControl<string>('');
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _panelScrollEl: HTMLElement | null = null;
    private _panelScrollHandler: (() => void) | null = null;
    private _loadMoreCooldownUntil = 0;
    private _pendingScrollRestore: number | null = null;
    private _loadTriggeredFromBottom = false;
    private _fillAttempts = 0;
    private _autoFillDisabled = false;
    private readonly _maxFillAttempts = 8;
    private readonly _scrollThreshold = 48;

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _translocoService: TranslocoService,
        private _ngZone: NgZone
    ) {}

    ngOnInit() {
        this._translocoService.langChanges$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((res) => {
                this.lang = res;
                this._changeDetectorRef.markForCheck();
            });
        this.syncDisplayOptions();

        this.filterCtrl.valueChanges
            .pipe(debounceTime(300), takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                if (this.serverSide) {
                    this._fillAttempts = 0;
                    this._autoFillDisabled = false;
                    this.searchChange.emit((this.filterCtrl.value ?? '').trim());
                    this.syncDisplayOptions();
                    this._changeDetectorRef.markForCheck();
                    return;
                }
                this.filterOptions();
            });
    }

    onPanelOpenedChange(opened: boolean): void {
        if (opened && this.serverSide) {
            this._fillAttempts = 0;
            this._autoFillDisabled = false;
            this._loadTriggeredFromBottom = false;
            setTimeout(() => {
                this._attachPanelScrollListener();
                this._scheduleFillIfShort();
            }, 0);
            return;
        }
        this._fillAttempts = 0;
        this._autoFillDisabled = false;
        this._loadTriggeredFromBottom = false;
        this._detachPanelScrollListener();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.options) {
            const prev = changes.options.previousValue as any[] | undefined;
            const next = changes.options.currentValue as any[] | undefined;
            const isAppend =
                this.serverSide &&
                Array.isArray(prev) &&
                Array.isArray(next) &&
                next.length > prev.length &&
                !changes.options.firstChange;
            if (
                isAppend &&
                this._panelScrollEl &&
                !this._loadTriggeredFromBottom
            ) {
                this._pendingScrollRestore = this._panelScrollEl.scrollTop;
            }
            if (isAppend) {
                this._fillAttempts = 0;
                this._autoFillDisabled = false;
            }
            this.syncDisplayOptions();
            if (!this.serverSide) {
                this.filterOptions();
            } else {
                this._changeDetectorRef.markForCheck();
            }
        }
        if (
            changes.loadingMore &&
            !changes.loadingMore.firstChange &&
            changes.loadingMore.currentValue === true &&
            this._panelScrollEl &&
            !this._loadTriggeredFromBottom
        ) {
            this._pendingScrollRestore = this._panelScrollEl.scrollTop;
        }
        if (
            changes.loadingMore &&
            !changes.loadingMore.firstChange &&
            changes.loadingMore.previousValue === true &&
            changes.loadingMore.currentValue === false
        ) {
            this._scheduleAfterLoadComplete();
        }
        if (
            changes.hasMore &&
            !changes.hasMore.firstChange &&
            changes.hasMore.currentValue === false
        ) {
            this._fillAttempts = 0;
            this._autoFillDisabled = true;
        }
        if (
            changes.hasMore &&
            !changes.hasMore.firstChange &&
            changes.hasMore.currentValue === true &&
            !changes.hasMore.previousValue
        ) {
            this._autoFillDisabled = false;
            this._scheduleFillIfShort();
        }
    }

    private syncDisplayOptions(): void {
        this.displayOptions = Array.isArray(this.options)
            ? this.options.slice()
            : [];
    }

    private _scheduleAfterLoadComplete(): void {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this._loadTriggeredFromBottom = false;
                // Keep the scroll position the user was at instead of snapping to the
                // new bottom - snapping there made the panel look "still at the bottom"
                // right after every append, which used to auto-chain into fetching every
                // remaining page back-to-back (see _maybeLoadMoreAtBottom, removed).
                this._scheduleScrollRestore();
                this._scheduleFillIfShort();
            });
        });
    }

    private _isNearBottom(panel: HTMLElement): boolean {
        return (
            panel.scrollTop + panel.clientHeight >=
            panel.scrollHeight - this._scrollThreshold
        );
    }

    private _emitLoadMore(fromBottom: boolean): void {
        if (!this.serverSide || !this.hasMore || this.loadingMore) {
            return;
        }
        const now = Date.now();
        if (now < this._loadMoreCooldownUntil) {
            return;
        }
        this._loadMoreCooldownUntil = now + 200;
        this._loadTriggeredFromBottom = fromBottom;
        const panel = this._getActivePanel();
        if (panel) {
            this._pendingScrollRestore = panel.scrollTop;
        }
        this._ngZone.run(() => this.loadMore.emit());
    }

    private _scheduleScrollRestore(): void {
        if (this._pendingScrollRestore == null) {
            return;
        }
        const top = this._pendingScrollRestore;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const panel = this._getActivePanel();
                if (panel) {
                    panel.scrollTop = top;
                    this._panelScrollEl = panel;
                }
                this._pendingScrollRestore = null;
            });
        });
    }

    private _getActivePanel(): HTMLElement | null {
        if (this._panelScrollEl?.isConnected) {
            return this._panelScrollEl;
        }
        const panels = document.querySelectorAll('.mat-mdc-select-panel');
        return (panels[panels.length - 1] as HTMLElement) ?? null;
    }

    /** When the list is shorter than the panel, scroll never fires — prefetch until scrollable or exhausted. */
    private _scheduleFillIfShort(): void {
        if (!this.serverSide || !this.hasMore || this.loadingMore) {
            return;
        }
        requestAnimationFrame(() => {
            requestAnimationFrame(() => this._fillIfShort());
        });
    }

    private _fillIfShort(): void {
        if (
            !this.serverSide ||
            !this.hasMore ||
            this.loadingMore ||
            this._autoFillDisabled
        ) {
            return;
        }
        if (this._fillAttempts >= this._maxFillAttempts) {
            this._autoFillDisabled = true;
            return;
        }
        const panel = this._getActivePanel();
        if (!panel || panel.clientHeight === 0) {
            return;
        }
        const overflow = panel.scrollHeight - panel.clientHeight;
        if (overflow > 4) {
            this._fillAttempts = 0;
            return;
        }
        this._fillAttempts += 1;
        this._emitLoadMore(true);
    }

    // Method to toggle "Select All"
    toggleSelectAll(event: any): void {
        event.stopPropagation();

        // Get the currently filtered options (visible options)
        let optionsToSelect = this.options;
        const searchValue = this.filterCtrl.value;
        if (searchValue) {
            optionsToSelect = this.options.filter(option =>
                this.optionMatchesSearch(option, searchValue.toLowerCase())
            );
        }

        const filteredValues = optionsToSelect.map(
            option => option[this.valueField]
        );
        const currentValues = (this.formControl.value || []).filter(
            v => v !== '__select_all__'
        );

        // Check if all filtered options are already selected
        const allFilteredSelected = filteredValues.every(val =>
            currentValues.includes(val)
        );

        if (allFilteredSelected) {
            // Deselect all filtered options
            const remainingValues = currentValues.filter(
                val => !filteredValues.includes(val)
            );
            this.formControl.setValue(remainingValues);
        } else {
            // Select all filtered options (merge with existing)
            const newValues = [
                ...new Set([...currentValues, ...filteredValues]),
            ];

            // Always set actual option values only (never __select_all__ in payload)
            this.formControl.setValue(newValues);
        }
        this.changeValue(); // Emit changes
    }

    isAllSelected(): boolean {
        const currentValues = (this.formControl.value || []).filter(
            v => v !== '__select_all__'
        );
        return (
            this.options?.length > 0 &&
            currentValues?.length === this.options?.length
        );
    }

    // Method to programmatically select all options
    selectAllOptions(): void {
        const allValues = this.options.map(option => option[this.valueField]);
        this.formControl.setValue(allValues);
        this.changeValue(); // Emit changes
    }

    /**
     * Label for the synthetic "All …" option (value 0).
     * Prefer a full phrase key (`All Agents`) so Arabic is not built by concatenating fragments.
     */
    getAllOptionLabel(): string {
        const placeholder = (this.searchPlaceholder ?? '').trim();
        if (!placeholder) {
            return this._translocoService.translate('All');
        }

        const phraseKeys = [
            `All ${placeholder}`,
            // Existing keys often use a lowercase second word (`All users`, `All actions`).
            `All ${placeholder.charAt(0).toLowerCase()}${placeholder.slice(1)}`,
        ];
        for (const key of phraseKeys) {
            const translated = this._translocoService.translate(key);
            if (translated !== key) {
                return translated;
            }
        }

        // Call sites sometimes pass an already-translated placeholder (e.g. t('Agents')).
        const fromLocalized: Record<string, string> = {
            'الوكلاء': 'All Agents',
            'المالكون': 'All Owners',
            'الأقسام': 'All Departments',
            'المستخدمون': 'All users',
            'الإجراءات': 'All actions',
            'أنواع الكيانات': 'All entity types',
        };
        const mappedKey = fromLocalized[placeholder];
        if (mappedKey) {
            const mapped = this._translocoService.translate(mappedKey);
            if (mapped !== mappedKey) {
                return mapped;
            }
        }

        // Last resort: join with an explicit space (do not rely on trailing spaces in i18n values).
        const allPrefix = this._translocoService
            .translate('All ')
            .replace(/\s+$/u, '');
        const itemLabel = this._translocoService.translate(placeholder);
        return `${allPrefix} ${itemLabel}`.replace(/\s+/gu, ' ').trim();
    }

changeValue() {
    const value = this.formControl.value;
    if (Array.isArray(value)) {
        const cleanedValue = value.filter(v => v !== '__select_all__');

        const allSelected =
            this.options.length > 0 &&
            cleanedValue.length === this.options.length;

        // if (allSelected && !value.includes('__select_all__')) {
        //     this.formControl.setValue([...cleanedValue, '__select_all__'], {
        //         emitEvent: false,
        //     });
        // } else if (!allSelected && value.includes('__select_all__')) {
        //     this.formControl.setValue(cleanedValue, { emitEvent: false });
        // }

        this.selectionChange.emit(cleanedValue);
        this.selectionObjectsChange.emit(
            this.mapValuesToObjects(cleanedValue)
        );
    } else {
        this.selectionChange.emit(value);
        // Optional single-select object emit
        const obj = this.options?.find(
            opt => opt[this.valueField] === value
        );
        this.selectionObjectsChange.emit(obj ? [obj] : []);
    }
}

    private mapValuesToObjects(values: any[]): any[] {
    if (!Array.isArray(values) || !Array.isArray(this.options)) {
        return [];
    }

    return values
        .map(value =>
            this.options.find(
                opt => opt[this.valueField] === value
            )
        )
        .filter(Boolean);
}


    ngOnDestroy(): void {
        this._detachPanelScrollListener();
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    private _attachPanelScrollListener(): void {
        this._detachPanelScrollListener();
        const panels = document.querySelectorAll('.mat-mdc-select-panel');
        const panel = panels[panels.length - 1] as HTMLElement | undefined;
        if (!panel) {
            return;
        }
        this._panelScrollEl = panel;
        this._panelScrollHandler = (): void => {
            if (!this.hasMore || this.loadingMore) {
                return;
            }
            if (!this._isNearBottom(panel)) {
                return;
            }
            this._emitLoadMore(true);
        };
        panel.addEventListener('scroll', this._panelScrollHandler, {
            passive: true,
        });
    }

    private _detachPanelScrollListener(): void {
        if (this._panelScrollEl && this._panelScrollHandler) {
            this._panelScrollEl.removeEventListener(
                'scroll',
                this._panelScrollHandler
            );
        }
        this._panelScrollEl = null;
        this._panelScrollHandler = null;
    }

    protected filterOptions(): void {
        if (!this.options) {
            return;
        }
        if (this.serverSide) {
            this.syncDisplayOptions();
            this._changeDetectorRef.markForCheck();
            return;
        }
        let search = this.filterCtrl.value;
        if (!search) {
            this.displayOptions = this.options.slice();
            this._changeDetectorRef.markForCheck();
            return;
        } else {
            search = search.toLowerCase();
        }
        this.displayOptions = this.options.filter(option =>
            this.optionMatchesSearch(option, search)
        );
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Resolve a dotted path (`name.en`). If an intermediate value is a plain
     * string/number (API migrated off `{en,ar}` objects), return that value
     * instead of reading further — so stale `name.en` bindings still show the name.
     */
    getNestedProperty(obj: unknown, path: string): unknown {
        return path.split('.').reduce((acc: unknown, part: string) => {
            if (acc == null) {
                return acc;
            }
            if (typeof acc !== 'object') {
                return acc;
            }
            return (acc as Record<string, unknown>)[part];
        }, obj);
    }

    /**
     * Turn a resolved display field into visible option text.
     * `{en,ar}` name objects and `{label|name|value}` rows must not render as
     * `[object Object]` (blank-looking labels in the panel and trigger).
     */
    formatDisplayValue(value: unknown): string {
        if (value == null) {
            return '';
        }
        if (
            typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean'
        ) {
            const text = String(value).trim();
            return text === '[object Object]' ? '' : text;
        }
        if (Array.isArray(value)) {
            return value
                .map(item => this.formatDisplayValue(item))
                .filter(item => item.length > 0)
                .join(', ');
        }
        if (typeof value !== 'object') {
            return '';
        }
        const rec = value as Record<string, unknown>;
        const localized = rec[this.lang] ?? rec['en'] ?? rec['ar'];
        if (typeof localized === 'string' || typeof localized === 'number') {
            return String(localized).trim();
        }
        for (const key of ['label', 'name', 'value', 'viewValue', 'text']) {
            const inner = rec[key];
            if (inner != null && inner !== value) {
                const formatted = this.formatDisplayValue(inner);
                if (formatted) {
                    return formatted;
                }
            }
        }
        return '';
    }

    getOptionLabel(option: unknown, field?: string): string {
        const path = field || this.displayField;
        const primary = this.formatDisplayValue(
            this.getNestedProperty(option, path)
        );
        if (primary) {
            return primary;
        }
        if (path !== 'label') {
            const label = this.formatDisplayValue(
                this.getNestedProperty(option, 'label')
            );
            if (label) {
                return label;
            }
        }
        if (path !== 'value') {
            const value = this.formatDisplayValue(
                this.getNestedProperty(option, 'value')
            );
            if (value) {
                return value;
            }
        }
        if (path !== 'name') {
            const name = this.formatDisplayValue(
                this.getNestedProperty(option, 'name')
            );
            if (name) {
                return name;
            }
        }
        return '';
    }

    private optionMatchesSearch(option: any, search: string): boolean {
        const primary = this.getOptionLabel(option).toLowerCase();
        if (primary.includes(search)) {
            return true;
        }
        if (!this.subtitleField) {
            return false;
        }
        const subtitle = this.getOptionLabel(
            option,
            this.subtitleField
        ).toLowerCase();
        return subtitle.includes(search);
    }

    // ControlValueAccessor implementation
    writeValue(value: any): void {
        // if (value !== undefined) {
        //   this.formControl.setValue(value, { emitEvent: false });
        // }
    }

    registerOnChange(fn: any): void {
        // this.formControl.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe(fn);
    }

    registerOnTouched(fn: any): void {}

    setDisabledState?(isDisabled: boolean): void {
        // isDisabled ? this.formControl.disable() : this.formControl.enable();
    }

    trackByOption(index: number, option: any): any {
        return option[this.valueField];
    }

    isOptionSelected(value: any): boolean {
        if (this.multiple) {
            const values = this.formControl.value;
            return Array.isArray(values) && values.includes(value);
        }
        return this.formControl.value === value;
    }

    /**
     * Closed-field label (primary / triggerDisplayField only — never subtitle).
     * Subtitles stay in the open panel so departments/emails/etc. help distinguish
     * options without cluttering the selected value in the form field.
     */
    getDisplayText(): string {
        const triggerField = this.triggerDisplayField || this.displayField;
        const raw = this.formControl.value;

        if (this.multiple) {
            const values = (Array.isArray(raw) ? raw : []).filter(
                (v) => v !== '__select_all__'
            );
            if (values.length === 0) {
                return '';
            }

            return values
                .map((value) => {
                    const option = this.options.find(
                        (opt) => opt[this.valueField] === value
                    );
                    if (!option) {
                        return '';
                    }
                    return this.getOptionLabel(option, triggerField);
                })
                .filter((label) => label.length > 0)
                .join(', ');
        }

        if (raw === null || raw === undefined || raw === '') {
            return '';
        }

        const option = this.options?.find(
            (opt) => opt[this.valueField] === raw
        );
        return option ? this.getOptionLabel(option, triggerField) : '';
    }
}
